import { DataTable, Dialog, FormLabel, Heading, Loader, Select } from "@/components";
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Label } from "@/components/ui";
import { cn } from "@/lib/utils";
import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import {
  useCopyFinancialTemplateMutation,
  useCreateFinancialTemplateMutation,
  useDeleteFinancialTemplateMutation,
  useGetCommunities,
  useGetFinancialTemplates,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, MoreVertical, Pencil, Plus, Search, Trash2, Eye } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const errorMessage = (e: any) => (typeof e?.response?.data?.message === "string" ? e.response.data.message : "Something went wrong");
const errorToast = (e: any) => toast.error(errorMessage(e));

export const StatusBadge = ({ status }: { status?: string }) => (
  <Badge variant="outline" className={cn("rounded-full px-3",
    status === "PUBLISHED" ? "border-green-600 bg-green-100 text-green-700" : "border-blue-500 bg-blue-50 text-blue-700")}>
    {status === "PUBLISHED" ? "Published" : "Draft"}
  </Badge>
);

/**
 * Master Data > Financial & Billing > Financial Statement Versions (admin): the community's report structures
 * (Category > Subcategory > GL accounts). Drafts are edited in the builder; published versions are read-only
 * and changed through a New Version.
 */
const FinancialStatementVersions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = community?.communityId && community.communityId != "all" ? String(community.communityId) : undefined;
  const { data: communityList, isLoading: isCommunityLoading } = useGetCommunities({});
  const { data: versions = [], isLoading } = useGetFinancialTemplates({ communityId });

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [createError, setCreateError] = useState("");
  const [toDelete, setToDelete] = useState<any>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["financial-templates", communityId] });
  const openBuilder = (id: number | string) => navigate(String(id));

  const { mutate: create, isPending: isCreating } = useCreateFinancialTemplateMutation({
    onError: (e) => setCreateError(errorMessage(e)),
    onSuccess: (res: any) => {
      setCreateOpen(false);
      refresh();
      openBuilder(res?.data?.data?.financialTemplateId);
    },
  });
  const { mutate: copy } = useCopyFinancialTemplateMutation({
    onError: errorToast,
    onSuccess: (res: any) => {
      const v = res?.data?.data;
      toast.success(`Version ${v?.versionNo} created as a draft`);
      refresh();
      openBuilder(v?.financialTemplateId);
    },
  });
  const { mutate: remove, isPending: isDeleting } = useDeleteFinancialTemplateMutation({
    onError: errorToast,
    onSuccess: () => {
      toast.success("Draft deleted");
      setToDelete(null);
      refresh();
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...versions]
      .filter((v: any) => !q || `${v.title} ${v.description || ""}`.toLowerCase().includes(q))
      .sort((a: any, b: any) => a.title.localeCompare(b.title) || b.versionNo - a.versionNo);
  }, [versions, search]);

  const columns = [
    { accessorKey: "title", header: "Financial Version Name",
      cell: ({ row }: any) => (
        <div>
          <p className="font-medium text-primary">{row.original.title}</p>
          {row.original.description && <p className="text-xs text-gray-500">{row.original.description}</p>}
        </div>
      ) },
    { accessorKey: "versionNo", header: "Version", cell: ({ row }: any) => `v${row.original.versionNo}` },
    { accessorKey: "status", header: "Status", cell: ({ row }: any) => <StatusBadge status={row.original.status} /> },
    { accessorKey: "accountCategoryCount", header: "Categories" },
    { accessorKey: "reportingGroupCount", header: "Subcategories" },
    { accessorKey: "associatedGlCount", header: "GL Accounts" },
    { accessorKey: "updatedAt", header: "Last Updated",
      cell: ({ row }: any) => (row.original.updatedAt ? moment(row.original.updatedAt).format("MM/DD/YYYY h:mm A") : "—") },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const v = row.original;
        const isDraft = v.status !== "PUBLISHED";
        return (
          <DropdownMenu>
            <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-0 shadow-md" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem className="flex items-center gap-2" onClick={() => openBuilder(v.financialTemplateId)}>
                {isDraft ? <><Pencil className="w-4 h-4" /> Edit</> : <><Eye className="w-4 h-4" /> View</>}
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-2" onClick={() => copy({ financialTemplateId: String(v.financialTemplateId) })}>
                <Copy className="w-4 h-4" /> New Version
              </DropdownMenuItem>
              {isDraft && (
                <DropdownMenuItem className="flex items-center gap-2 text-red-600" onClick={() => setToDelete(v)}>
                  <Trash2 className="w-4 h-4" /> Delete Draft
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onCommunityChange = (value: any) => {
    const details = communityList?.data?.find((c: any) => c.communityId == value);
    dispatch(setCommunityData(details));
  };

  const openCreate = () => {
    setTitle("");
    setDescription("");
    setCreateError("");
    setCreateOpen(true);
  };

  const onCreate = () => {
    if (!title.trim()) {
      setCreateError("Financial Version Name is required.");
      return;
    }
    create({ communityId: Number(communityId), title: title.trim(), description: description.trim() || null });
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between mb-6">
        <div>
          <Heading title="Financial Statement Versions" />
          <p className="text-sm text-gray-500">Report structures for a community: categories, subcategories and GL accounts in the order reports show them.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-full sm:w-72">
            <FormLabel>Select Community</FormLabel>
            <Select
              options={communityList?.data?.map((c: any) => ({ value: c?.communityId?.toString(), label: c?.name }))}
              value={communityId ?? ""}
              onChange={onCommunityChange}
              className="h-[36px] w-full"
              disabled={isCommunityLoading}
            />
          </div>
          <Input type="text" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 bg-white h-[40px]" startIcon={<Search className="w-4 h-4" />} />
          <Button onClick={openCreate} disabled={!communityId}><Plus className="w-4 h-4" /> New Financial Version</Button>
        </div>
      </div>

      {!communityId ? (
        <p className="text-sm text-gray-500">Select a community to see its financial statement versions.</p>
      ) : isLoading ? (
        <Loader />
      ) : (
        <DataTable data={rows} columns={columns} onRowClick={(row: any) => openBuilder(row.original.financialTemplateId)} />
      )}

      <Dialog open={createOpen} setOpen={setCreateOpen} hideFooter title="New Financial Version">
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500">Created as a draft in {community?.name}. You'll build the categories next.</p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fsv-title">Financial Version Name *</Label>
            <Input id="fsv-title" maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. FY 2027 Management Reporting" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fsv-desc">Description</Label>
            <Input id="fsv-desc" maxLength={255} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {createError && <p className="text-sm text-red-600">{createError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={isCreating}>Cancel</Button>
            <Button onClick={onCreate} disabled={isCreating}>Create &amp; Build</Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={!!toDelete} setOpen={(o: boolean) => !o && setToDelete(null)} hideFooter title="Delete draft?">
        <div className="flex flex-col gap-4">
          <p className="text-sm">"{toDelete?.name}" and its structure will be deleted. This can't be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" disabled={isDeleting}
              onClick={() => remove({ financialTemplateId: String(toDelete.financialTemplateId) })}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinancialStatementVersions;
